// Lightweight top-level ErrorBoundary specifically designed to recover from
// stale-HTML / missing-chunk failures after a split-bundle deploy.
//
// What it does (and ONLY this):
//   • Catches render/lifecycle errors that escape Suspense.
//   • If the error looks like a ChunkLoadError (stale HTML referencing a
//     chunk filename that no longer exists on the CDN), it triggers ONE
//     safe page reload so the browser pulls fresh index.html + fresh
//     chunk hashes.
//   • The reload is gated by a sessionStorage flag — we will NEVER reload
//     more than once per browser session, no matter what.
//   • For non-chunk errors, it renders a small inline notice and lets the
//     browser's normal error reporting do its thing (no auto-reload).
//
// Why a class component:
//   React error boundaries MUST be classes. There is no functional
//   equivalent today. Kept as small as possible.
import React from 'react';

const RELOAD_FLAG = 'fs_chunk_recovery_reloaded';

// Heuristic: did this look like a missing-chunk failure from React.lazy?
// We match conservatively — only the three canonical signatures.
const isChunkLoadError = (err) => {
  if (!err) return false;
  const name = err.name || '';
  const msg = (err.message || '').toLowerCase();
  if (name === 'ChunkLoadError') return true;
  if (msg.includes('loading chunk') && msg.includes('failed')) return true;
  if (msg.includes('failed to fetch dynamically imported module')) return true;
  if (msg.includes('importing a module script failed')) return true;
  return false;
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, isChunkError: false };
  }

  static getDerivedStateFromError(error) {
    return { error, isChunkError: isChunkLoadError(error) };
  }

  componentDidCatch(error, info) {
    // If a chunk failed to load and we have NOT already attempted a
    // recovery reload this session, do it exactly once.
    if (isChunkLoadError(error) && typeof window !== 'undefined') {
      try {
        const already = sessionStorage.getItem(RELOAD_FLAG);
        if (!already) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          // Tiny delay so the fallback UI paints first — purely cosmetic.
          window.setTimeout(() => window.location.reload(), 200);
          return;
        }
      } catch {
        // sessionStorage blocked (Safari private mode etc.) — fall through.
      }
    }
    // Log for observability; never throw from inside the handler.
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, info);
  }

  render() {
    if (!this.state.error) return this.props.children;

    // Tiny inline fallback. No external CSS / no Tailwind dependency so
    // the fallback works even if the CSS chunk also failed to load.
    const wrap = {
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily:
        '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      color: '#334155',
      background: '#ffffff',
      padding: '24px',
      textAlign: 'center',
    };
    const dotStyle = {
      width: 32,
      height: 32,
      margin: '0 auto 16px',
      border: '3px solid #fee2e2',
      borderTopColor: '#f43f5e',
      borderRadius: '50%',
      animation: 'fsSpin 0.9s linear infinite',
    };

    return (
      <div role="alert" aria-busy="true" style={wrap} data-testid="app-error-boundary">
        <div>
          <style>{`@keyframes fsSpin{to{transform:rotate(360deg)}}`}</style>
          <div style={dotStyle} />
          <p style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>
            {this.state.isChunkError
              ? 'Refreshing application…'
              : 'Something went wrong.'}
          </p>
          {!this.state.isChunkError && (
            <p style={{ fontSize: 13, marginTop: 8, color: '#64748b' }}>
              Please refresh the page. If the problem persists, contact support.
            </p>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
